// @flow
import * as React from 'react';
import dynamic from 'next/dynamic';

import config from 'Config';
import { useRouter } from 'Client/router/useRouter';
import ReactAppContext from 'Context/ReactAppContext';
import { GOOGLE_API_STATIC_PATH, GOOGLE_API_KEY } from 'Components/utils/google/google.constants';
import { getStaticMapURL } from 'Components/utils/google/google.utils';

import { Map } from './Map.jsx';

const GoogleMapReact = dynamic(() => import('google-map-react'), {
  ssr: false,
});

export type Props = {
  className?: string,
  closeLabel?: string,
  format?: string,
  height?: number,
  latitude?: number,
  longitude?: number,
  maptype?: string,
  scale?: number,
  title?: string,
  width?: number,
  zoom?: number,
};

const createMapOptions = () => {
  return {
    fullscreenControl: false,
  };
};

const addMarker = ({ map, maps, position, icon }) => {
  // eslint-disable-next-line no-new
  new maps.Marker({
    icon,
    map,
    position,
  });
};

export const GoogleMap = (props: Props) => {
  const {
    className,
    closeLabel,
    format = 'jpeg',
    height = 420,
    latitude = 0,
    longitude = 0,
    maptype = 'roadmap',
    scale = 2,
    title,
    width = 780,
    zoom = 8,
  } = props;

  const coordinates = { lat: latitude, lng: longitude };
  const appContext = React.useContext(ReactAppContext);
  const { location } = useRouter<PageWithLinesUrlParams, ProductQueryParams>();

  const iconUrl = `${location.origin}/markers/marker.png`;
  const staticMapOptions = {
    center: `${latitude},${longitude}`,
    zoom,
    size: `${width}x${height}`,
    scale,
    maptype,
    language: appContext.locale,
    format,
    markers: `icon:${iconUrl}|scale:${scale}|${latitude},${longitude}`,
  };

  return (
    <Map
      className={className}
      closeLabel={closeLabel}
      dataTestId="map-preview"
      imageSrc={getStaticMapURL(GOOGLE_API_STATIC_PATH, staticMapOptions)}
      ratio={{ w: width, h: height }}
      title={title}
      trackingData={{
        categoryGA: 'resort',
        actionGA: 'mapClick',
        labelGA: 'GoogleMap',
      }}
    >
      <div className="h-full">
        <GoogleMapReact
          bootstrapURLKeys={{ key: config.get(GOOGLE_API_KEY) }}
          defaultCenter={coordinates}
          defaultZoom={zoom}
          onGoogleApiLoaded={({ map, maps }) =>
            addMarker({ map, maps, position: coordinates, icon: iconUrl })
          }
          options={createMapOptions}
          yesIWantToUseGoogleMapApiInternals
        />
      </div>
    </Map>
  );
};
